import { useEffect, useState } from "react";
import { t } from "@/lib/strings";

export interface SectionNavItem {
  id: string;
  title: string;
  entryCount: number;
}

interface Props {
  sections: SectionNavItem[];
  canEdit: boolean;
  onAddSection?: () => void;
}

function scrollToSection(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

/** Left rail beside the memo block list: one nav item per section (title +
 * entry count, or "—" when empty). Clicking scrolls the corresponding block
 * into view; the item for the section currently in view (or last clicked)
 * is highlighted. Purely a navigation aid — it never mutates memo content
 * except via the caller-supplied `onAddSection` (the page's existing
 * add-block mutation). */
export function SectionNavRail({ sections, canEdit, onAddSection }: Props) {
  const [active, setActive] = useState<string | null>(sections[0]?.id ?? null);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const ids = sections.map((s) => s.id);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.find((e) => e.isIntersecting);
        if (visible) {
          setActive(visible.target.id);
        }
      },
      { rootMargin: "-15% 0px -70% 0px" },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [sections]);

  return (
    <nav
      aria-label={t.seriesSectionsLabel}
      style={{
        width: 230,
        flexShrink: 0,
        borderRight: "1px solid var(--hairline)",
        paddingRight: 16,
        alignSelf: "flex-start",
        position: "sticky",
        top: 24,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: ".1em",
          textTransform: "uppercase",
          color: "var(--ink-4)",
          marginBottom: 8,
        }}
      >
        {t.seriesSectionsLabel}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {sections.map((s) => {
          const isActive = active === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => {
                setActive(s.id);
                scrollToSection(s.id);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                border: "none",
                borderRadius: 6,
                padding: "7px 8px",
                fontSize: 13,
                textAlign: "left",
                cursor: "pointer",
                background: isActive ? "var(--surface-soft)" : "transparent",
                color: "var(--ink-1)",
              }}
            >
              <span
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                {s.title}
              </span>
              <span style={{ color: "var(--ink-4)", fontSize: 12, flexShrink: 0 }}>
                {s.entryCount > 0 ? s.entryCount : "—"}
              </span>
            </button>
          );
        })}
      </div>
      {canEdit && onAddSection ? (
        <button
          type="button"
          onClick={onAddSection}
          style={{
            marginTop: 10,
            width: "100%",
            background: "transparent",
            border: "1px dashed var(--hairline)",
            borderRadius: 8,
            padding: "7px 10px",
            fontSize: 12.5,
            color: "var(--ink-2)",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          {t.memoAddBlock}
        </button>
      ) : null}
    </nav>
  );
}
